import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Checkbox, Field } from "@cntrl/ui";
import { useAuth } from "../../app/auth/useAuth.js";
import { AuthError } from "../../app/auth/session.js";
import { passkeySupported, isPasskeyCancel, isPasskeyAlreadyEnrolled } from "../../app/auth/passkey.js";
import { AuthCard } from "./AuthCard.js";
import styles from "./AuthForm.module.scss";

const schema = z.object({
  email: z.string().min(1, "Enter your email").email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});
type LoginValues = z.infer<typeof schema>;

const codeSchema = z.object({
  code: z
    .string()
    .min(1, "Enter the code from your email")
    .regex(/^\d{6}$/, "The code is 6 digits"),
});
type CodeValues = z.infer<typeof codeSchema>;

/** The spec's resend cooldown ("Resend code in 30s"). An affordance over the server cap, not the cap. */
const RESEND_COOLDOWN_SECONDS = 30;

/** The emailed-OTP challenge held between the two steps. Never contains the password. */
interface OtpStep {
  loginToken: string;
  email: string;
}

export interface LoginScreenProps {
  /** Navigate onward after a successful login (route wires this to the redirect target). */
  onAuthenticated: () => void;
  onForgotPassword: () => void;
  /** Render inside a dialog (the /apply page's log-in, ART-307) rather than as the full-screen gate. The
   *  post-login Touch ID offer is skipped there: the dialog closes on sign-in and returns to the form. */
  embedded?: boolean;
}

/**
 * The login screen wired to POST /auth/login — one screen, two steps. Step 1 (credentials) completes
 * directly while `auth.otp_required` is off / the address is exempt / the passkey path ran; when the
 * server answers with the emailed-OTP challenge instead, step 2 collects the 6-digit code and completes
 * via POST /auth/verify-otp. "Resend code" re-submits the held credentials for a fresh challenge
 * (the backend mints per login call — no dedicated resend endpoint), behind the spec's 30s countdown.
 * Surfaces the server error verbatim except 401s, which get a generic line (no oracle on which half
 * was wrong).
 */
export function LoginScreen({ onAuthenticated, onForgotPassword, embedded = false }: LoginScreenProps) {
  const auth = useAuth();
  // Touch ID / Face ID is offered only where a platform authenticator actually exists — probed async,
  // so the button never flashes in on a device that cannot honour it.
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // The post-login "Set up Touch ID?" step, exactly where the specimen offers it. Dismissal lives in
  // component state ONLY — the specimen kept a `sessionStorage` flag, which our lint bans outright, and
  // an in-memory "already asked" is the honest scope anyway: it should not outlive the tab.
  const [enrolOffer, setEnrolOffer] = useState(false);
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [enrolError, setEnrolError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void passkeySupported().then((ok) => { if (live) setPasskeyReady(ok); });
    return () => { live = false; };
  }, []);
  const [serverError, setServerError] = useState<string | null>(null);
  const [otpStep, setOtpStep] = useState<OtpStep | null>(null);
  // "Trust this device" on the code step (ART-317): unticked by default; ticked, the next password sign-in on
  // this device skips the code for the trusted-device window.
  const [trustDevice, setTrustDevice] = useState(false);
  const [resending, setResending] = useState(false);
  /**
   * Seconds left on the resend affordance (the spec's "Resend code in 30s"). This is UX layered OVER
   * the server limiter, NEVER a substitute for it: `auth-otp-issue` is 5 codes / 10 min per account and
   * fails closed. Without it the button is uncapped client-side and an eager user just earns an opaque 429.
   */
  const [cooldown, setCooldown] = useState(0);
  const {
    register,
    handleSubmit,
    getValues,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(schema), mode: "onSubmit" });
  const {
    register: registerCode,
    handleSubmit: handleCodeSubmit,
    reset: resetCode,
    formState: { errors: codeErrors, isSubmitting: isVerifying },
  } = useForm<CodeValues>({ resolver: zodResolver(codeSchema), mode: "onSubmit" });

  // One interval for the whole countdown, started when it begins and cleared when it lands on 0 (or the
  // screen unmounts) — deliberately NOT a per-second chain of timeouts, which would re-arm only as fast
  // as React re-renders.
  const counting = cooldown > 0;
  useEffect(() => {
    if (!counting) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [counting]);

  // The gate is a single-purpose page whose only content is this form, so the live field takes focus on
  // arrival (the spec autoFocuses email, and moves focus to the code field when the challenge lands).
  // On the code step this is a fix, not just parity: the keyed remount below drops focus to <body>.
  // Done with a ref + effect because the `autoFocus` attribute is banned repo-wide by jsx-a11y.
  const emailRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const onCodeStep = otpStep !== null;
  useEffect(() => {
    (onCodeStep ? codeRef : emailRef).current?.focus();
  }, [onCodeStep]);

  /**
   * Touch ID / Face ID sign-in. The typed address is passed only to SCOPE the credential list; it is not
   * required, and we never ask whether an address has a passkey (that question is an enumeration oracle
   * — see app/auth/passkey.ts). A cancelled prompt is the common case and stays SILENT: the user simply
   * carries on with their password.
   */
  const onPasskey = async () => {
    setServerError(null);
    setPasskeyBusy(true);
    try {
      await auth.loginWithPasskey({ email: getValues("email") });
      onAuthenticated();   // they already hold a passkey — never offer enrolment
    } catch (err) {
      if (isPasskeyCancel(err)) return;   // dismissed the prompt — not a failure, say nothing
      setServerError(
        err instanceof AuthError
          ? "Touch ID didn't work. Sign in with your password instead."
          : "Something went wrong on this device — check your connection and try again.",
      );
    } finally {
      setPasskeyBusy(false);
    }
  };

  /**
   * Enrol from the post-login offer, on an ALREADY-AUTHENTICATED session — so a failure must never strand
   * the user. It must not be SILENT either, and it was: every error was swallowed and the user handed into
   * the app regardless, which made a failed enrolment indistinguishable from a successful one. The only
   * symptom then arrives days later as "Touch ID doesn't work", with nothing to go on — which is exactly
   * how ART-7 was reported and why it took a live probe to diagnose (2026-09-06).
   *
   * Two outcomes, and collapsing them is the bug: a CANCELLED prompt is a normal "no thanks" and proceeds
   * without a word, while a real failure keeps them on this card and says so. Continuing is always one tap
   * away — the session is good either way — but the user now knows their passkey did not enrol.
   */
  const onEnrol = async () => {
    setEnrolBusy(true);
    setEnrolError(null);
    try {
      await auth.enrolPasskey();
      onAuthenticated();
    } catch (err) {
      // Declined, or already enrolled on this authenticator — both mean "nothing to do here", and neither
      // is a failure. `InvalidStateError` is the one that bites repeatedly: the offer is made after every
      // password login, so once you HAVE a passkey every later login used to end in an error telling you
      // setup failed, when in fact it had already succeeded (observed 2026-09-06, right after the first
      // successful enrolment). Skipping the offer entirely is the real fix — see `useHasPasskey` — and this
      // stays as the backstop for the cases that read can't see: a credential enrolled in another session,
      // or a race between the read and the tap.
      if (isPasskeyCancel(err) || isPasskeyAlreadyEnrolled(err)) {
        onAuthenticated();
        return;
      }
      setEnrolError(
        err instanceof AuthError
          ? "Touch ID couldn't be set up on this device. You can try again from Settings."
          : "Touch ID couldn't be set up — check your connection and try again.",
      );
    } finally {
      setEnrolBusy(false);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const result = await auth.login(values);
      if (result.kind === "otp") {
        setOtpStep({ loginToken: result.loginToken, email: result.email });
        setCooldown(RESEND_COOLDOWN_SECONDS);
        return; // no session yet — the code step completes it
      }
      // The session is live. Offer Touch ID before leaving, where the specimen offers it — but only where
      // the platform can honour it AND the caller does not already have one. That second condition needs a
      // live session, which is why it is asked HERE rather than in the mount-time probe above: the read is
      // authenticated, and until this point there is no bearer to make it with.
      //
      // Without it the offer is made after EVERY password login, and once you have a passkey the platform
      // refuses the duplicate (`excludeCredentials`) — so the card became a guaranteed error on every login
      // after the first successful enrolment. `hasPasskey` answers false on failure, so a flaky read costs
      // an unnecessary offer rather than a broken handoff.
      if (!embedded && passkeyReady && !(await auth.hasPasskey())) {
        setEnrolOffer(true);
        return;
      }
      onAuthenticated();
    } catch (err) {
      // Never report success on a failed auth. Three shapes: a non-401 server error (verbatim), a 401
      // (generic — no oracle), and a NON-AuthError (network/native-storage failure — e.g. a Keychain
      // write that threw AFTER the server accepted; caught live in the simulator pass). The last must
      // never blame the credentials: the password was right and the server may already hold a session.
      // The spec clears the password on a rejected attempt — but ONLY on a 401. Clearing it on the
      // device-side branch would contradict that branch's whole reason for existing (the password may
      // have been RIGHT), and clearing it on the challenge path would break the resend, which re-logs-in
      // with the values still held in this form.
      if (err instanceof AuthError && err.status === 401) resetField("password");
      setServerError(
        err instanceof AuthError
          ? err.status !== 401
            ? err.message
            : "That email and password don't match. Try again."
          : "Something went wrong on this device — check your connection and try again.",
      );
    }
  });

  const onVerify = handleCodeSubmit(async ({ code }) => {
    if (!otpStep) return;
    setServerError(null);
    try {
      await auth.verifyOtp({ loginToken: otpStep.loginToken, code, trustDevice, email: otpStep.email });
      onAuthenticated();
    } catch (err) {
      // 401 = wrong/expired/burned code (the server burns it after the attempt cap); keep it generic.
      // A NON-AuthError is a device-side failure (network / native storage) — the code may have been
      // CORRECT and single-use-consumed server-side, so never tell the user to retype it.
      setServerError(
        err instanceof AuthError
          ? err.status !== 401
            ? err.message
            : "That code didn't work — check it or send a new one."
          : "Something went wrong on this device — go back and sign in again.",
      );
    }
  });

  /** A fresh challenge via re-login with the values still held in the credentials form. */
  const onResend = async () => {
    setServerError(null);
    setResending(true);
    try {
      const result = await auth.login(getValues());
      if (result.kind === "otp") {
        setOtpStep({ loginToken: result.loginToken, email: result.email });
        setCooldown(RESEND_COOLDOWN_SECONDS);
        resetCode();
      } else {
        // The policy flipped off between steps — the re-login just completed the session.
        onAuthenticated();
      }
    } catch (err) {
      setServerError(
        err instanceof AuthError ? err.message : "Couldn't send a new code. Try again.",
      );
    } finally {
      setResending(false);
    }
  };

  const backToPassword = () => {
    setOtpStep(null);
    setServerError(null);
    setCooldown(0);
    resetCode();
  };

  if (otpStep) {
    // The digit strip and the focus ref both need register()'s own handlers, so pull them apart rather
    // than relying on spread order — a later reorder would silently reinstate register's onChange.
    const { ref: codeFieldRef, onChange: onCodeFieldChange, ...codeField } = registerCode("code");
    return (
      // Keyed per step: the two steps render structurally identical trees, and WITHOUT a remount React
      // reuses the credentials <input> DOM node for the code field — its uncontrolled value (the typed
      // email) would survive into the code step (caught by the value-probe test).
      <AuthCard
        key="otp"
        embedded={embedded}
        title="Check your email"
        subtitle={`We sent a 6-digit code to ${otpStep.email}.`}
        footer={
          <Button variant="link" onClick={backToPassword} type="button">
            Back to password
          </Button>
        }
      >
        <form className={styles.form} onSubmit={onVerify} noValidate>
          <Field
            label="Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            error={codeErrors.code?.message}
            {...codeField}
            onChange={(e) => {
              // Spec: "Non-digits stripped; exactly 6 digits required." Strip on the DOM node BEFORE
              // handing the event to react-hook-form, so the stored value and what the user sees agree.
              // BOTH jobs belong here, strip THEN cap: a `maxLength` attribute would be applied by the
              // browser to the RAW paste, before this handler ever runs, so a code copied out of the
              // email with a leading space (or rendered "123 456") would be truncated to six raw
              // characters and lose its last digit — paste-to-sign-in silently broken.
              e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
              return onCodeFieldChange(e);
            }}
            ref={(el) => {
              codeFieldRef(el);
              codeRef.current = el;
            }}
          />
          <Checkbox checked={trustDevice} onChange={setTrustDevice} label="Trust this device" />
          {serverError && (
            <p className={styles.serverError} role="alert">
              {serverError}
            </p>
          )}
          <Button variant="accent" type="submit" fullWidth loading={isVerifying}>
            Verify code
          </Button>
          <Button
            variant="link"
            type="button"
            onClick={onResend}
            loading={resending}
            disabled={isVerifying || cooldown > 0}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </Button>
        </form>
      </AuthCard>
    );
  }

  // "Set up Touch ID?" — the specimen's own post-login step, reached only with a LIVE session, so there
  // is no credential on this card and nothing to leak. Declining is a first-class answer.
  if (enrolOffer) {
    return (
      <AuthCard
        key="enrol"
        embedded={embedded}
        title="Set up Touch ID?"
        subtitle="Sign in next time without a password or emailed code."
        footer={
          <Button variant="link" type="button" onClick={onAuthenticated} disabled={enrolBusy}>
            {enrolError ? "Continue" : "Not now"}
          </Button>
        }
      >
        <div className={styles.form}>
          {enrolError && (
            <p className={styles.serverError} role="alert">
              {enrolError}
            </p>
          )}
          <Button variant="accent" type="button" fullWidth loading={enrolBusy} onClick={onEnrol}>
            {enrolError ? "Try again" : "Enable Touch ID"}
          </Button>
        </div>
      </AuthCard>
    );
  }

  const { ref: emailFieldRef, ...emailField } = register("email");
  return (
    <AuthCard
      key="credentials"
      embedded={embedded}
      title="Welcome back"
      subtitle="Sign in to Artist CNTRL"
      footer={
        <Button variant="link" onClick={onForgotPassword} type="button">
          Forgot password?
        </Button>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Field
          label="Email"
          type="email"
          // `username`, not `antiAutofill`. The pair (username + current-password) is what lets iOS scope
          // a saved credential to the address typed here; without it the manager guesses and fills BOTH
          // fields, which is the wrong-account incident the old defense was built for. PO ruling, ART-7.
          autoComplete="username"
          inputMode="email"
          placeholder="you@band.com"
          error={errors.email?.message}
          {...emailField}
          ref={(el) => {
            emailFieldRef(el);
            emailRef.current = el;
          }}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register("password")}
        />
        {serverError && (
          <p className={styles.serverError} role="alert">
            {serverError}
          </p>
        )}
        <Button variant="accent" type="submit" fullWidth loading={isSubmitting}>
          Sign in
        </Button>
        {passkeyReady && (
          <Button
            variant="ghost"
            type="button"
            fullWidth
            className={styles.passkeyButton}
            loading={passkeyBusy}
            onClick={onPasskey}
          >
            Sign in with Touch ID
          </Button>
        )}
      </form>
    </AuthCard>
  );
}
